import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

/**
 * Token-styled modal. Focus-trap-lite via initial focus; Esc and click-outside close.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   title?: string,
 *   width?: 360|480|640|800|1100,
 *   footer?: any,
 *   children?: any,
 * }} props
 */
export default function Modal({ open, onClose, title, width = 480, footer, children }) {
  /** @type {React.RefObject<HTMLDivElement>} */
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    /** @param {KeyboardEvent} e */
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, background: 'var(--ab-overlay)',
        display: 'grid', placeItems: 'center', zIndex: 1000,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? 'Dialog' : undefined}
        tabIndex={-1}
        style={{
          background: 'var(--ab-bg)',
          border: 'var(--ab-hairline)',
          borderRadius: 'var(--ab-radius-lg)',
          boxShadow: 'var(--ab-shadow-lg)',
          width, maxWidth: '95vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          outline: 'none',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 24px',
          borderBottom: '1px solid var(--ab-border-hair)',
        }}>
          <div
            id={titleId}
            style={{
              flex: 1, fontSize: 'var(--ab-text-h4)',
              fontWeight: 'var(--ab-weight-semi)', color: 'var(--ab-fg)',
            }}
          >
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ab-btn"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--ab-fg-subtle)', display: 'inline-flex', padding: 4,
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: 24, overflow: 'auto' }}>{children}</div>
        {footer && (
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'flex-end',
            padding: '16px 24px',
            borderTop: '1px solid var(--ab-border-hair)',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
